import { Fragment, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Outlet } from "react-router-dom";
import { observer } from "mobx-react-lite";
import AppFrame from "./AppFrame";
import { useStore } from "../stores/store";
import { Loading } from "@shopify/polaris";
import LoginPage from "../../features/users/LoginPage";
import SettingsPage from "../../features/settings/SettingsPage";
import LandingPage from "../../features/home/LandingPage";
import { hasShopifyAuthParams, stripShopifyAuthParams } from "../utils/shopifyAuth";
import { rememberLastAppRoute } from "../utils/lastAppRoute";

const App = () => {
  const { commonStore, userStore } = useStore();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const loadUser = async () => {
      if (hasShopifyAuthParams(location.search)) {
        if (commonStore.token || userStore.isLoggedIn) {
          const nextSearch = stripShopifyAuthParams(location.search);
          const nextUrl = `${location.pathname}${nextSearch}${location.hash}`;
          if (nextUrl !== `${location.pathname}${location.search}${location.hash}`) {
            navigate(nextUrl, { replace: true });
          }

          if (commonStore.token && !userStore.isLoggedIn) {
            await userStore.getUser();
          }

          commonStore.setAppLoaded();
          return;
        }

        // Shopify launch params are authoritative. Re-run embedded login so
        // offline tokens and granted scopes refresh after app scope changes.
        commonStore.setToken(null);
        commonStore.setAppLoaded();
        return;
      }

      if (!commonStore.token) {
        commonStore.setAppLoaded();
        return;
      }

      await userStore.getUser();

      const params = new URLSearchParams(window.location.search);
      const urlShop = params.get("shop");
      if (urlShop && userStore.user?.shopDomain && urlShop !== userStore.user.shopDomain) {
        userStore.logout();
      }

      commonStore.setAppLoaded();
    };

    loadUser();
  }, [commonStore, location.hash, location.pathname, location.search, navigate, userStore]);

  useEffect(() => {
    if (userStore.isLoggedIn) {
      rememberLastAppRoute(location.pathname, location.search, location.hash);
    }
  }, [location.hash, location.pathname, location.search, userStore.isLoggedIn]);

  if (!commonStore.appLoaded) return (<Fragment><AppFrame><div style={{ height: '100px' }}><Loading /></div></AppFrame></Fragment>)

  if (!userStore.isLoggedIn) {
    if (window.location.pathname === '/login' || hasShopifyAuthParams(location.search)) {
      return (<Fragment><LoginPage /></Fragment>);
    }

    return (
      <Fragment>
        <LandingPage />
      </Fragment>
    );
  }

  return (
    <AppFrame>
      {window.location.pathname === '/' ? <SettingsPage /> : (
        <Outlet />
      )}
    </AppFrame>
  )

};

export default observer(App);
